import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Minus } from 'lucide-react';
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  CheckboxGroupProps as AriaCheckboxGroupProps,
  CheckboxRenderProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
  type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { FieldError, Label, labelVariants } from '../Field';

const checkboxVariants = cva('group/checkbox flex items-center gap-x-2', {
  variants: {
    disabled: {
      true: 'text-sm rac-disabled:cursor-not-allowed rac-disabled:opacity-70',
    },
  },
  defaultVariants: {
    disabled: true,
  },
});

const checkboxGroupVariants = cva('group flex flex-col gap-2');

/**
 * Props for the Checkbox component.
 * Extends AriaCheckboxProps with styling variants.
 */
interface CheckboxProps
  extends AriaCheckboxProps, VariantProps<typeof checkboxVariants> {}

/**
 * Checkbox component for binary or indeterminate selection.
 *
 * Provides an accessible checkbox with customizable styling and states.
 * Supports selected, unselected, and indeterminate states.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Checkbox>Accept terms</Checkbox>
 *
 * // With state management
 * <Checkbox isSelected={isChecked} onChange={setIsChecked}>
 *   Remember me
 * </Checkbox>
 *
 * // Indeterminate state
 * <Checkbox isIndeterminate>Partially selected</Checkbox>
 * ```
 *
 * @param props - Checkbox properties
 */
const Checkbox = ({ className, children, ...props }: CheckboxProps) => {
  const rootRef = React.useRef<HTMLLabelElement | null>(null);

  /**
   * This is a workaround to prevent the page from scrolling when the checkbox is focused
   * For more context: https://rematter-inc.slack.com/archives/C0101CV4Q8M/p1763054819227199
   */
  React.useEffect(() => {
    const label = rootRef.current;
    if (!label) return;

    const input = label.querySelector('input');
    if (!input) return;

    const originalFocus = input.focus.bind(input);

    input.focus = (options?: FocusOptions) => {
      originalFocus({
        ...(options ?? {}),
        preventScroll: true,
      });
    };

    return () => {
      input.focus = originalFocus;
    };
  }, []);

  return (
    <AriaCheckbox
      ref={rootRef}
      className={composeRenderProps(className, (className) =>
        cn('@Checkbox w-full', checkboxVariants(), labelVariants, className),
      )}
      {...props}
    >
      {composeRenderProps(
        children,
        (
          children,
          renderProps: CheckboxRenderProps & {
            defaultChildren: React.ReactNode | undefined;
          },
        ) => (
          <>
            <div
              className={cn(
                'ring-offset-background flex size-4 shrink-0 flex-col items-center justify-center rounded border border-primary text-white transition-colors',
                'group-data-[indeterminate]/checkbox:bg-primary group-data-[indeterminate]/checkbox:text-abs-white group-data-[selected]/checkbox:bg-primary group-data-[selected]/checkbox:text-abs-white',
                renderProps.isDisabled && 'cursor-not-allowed opacity-50',
                renderProps.isInvalid &&
                  'border-destructive group-data-[selected]/checkbox:bg-destructive group-data-[selected]/checkbox:text-abs-white',
                'focus:outline-none focus-visible:outline-none',
                renderProps.isFocused && 'ring-2 ring-primary/40',
              )}
            >
              {renderProps.isIndeterminate ? (
                <Minus className="size-4" />
              ) : renderProps.isSelected ? (
                <Check className="size-4" />
              ) : null}
            </div>
            {children}
          </>
        ),
      )}
    </AriaCheckbox>
  );
};

/**
 * Props for the CheckboxGroup component.
 * Extends AriaCheckboxGroupProps with styling variants and adds label, description, and error handling.
 */
interface CheckboxGroupProps
  extends AriaCheckboxGroupProps, VariantProps<typeof checkboxGroupVariants> {
  /**
   * Label text displayed above the checkbox group
   */
  label?: string;

  /**
   * Description text displayed below the label
   */
  description?: string;

  /**
   * Error message shown when validation fails. Can be a string or a function that returns a string based on validation results
   */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
}

/**
 * CheckboxGroup component for grouping related checkboxes.
 *
 * Provides an accessible container for multiple checkboxes with a shared label and validation.
 * Supports description text and error messages.
 *
 * @example
 * ```tsx
 * <CheckboxGroup label="Select options" description="Choose one or more options">
 *   <Checkbox value="option1">Option 1</Checkbox>
 *   <Checkbox value="option2">Option 2</Checkbox>
 *   <Checkbox value="option3">Option 3</Checkbox>
 * </CheckboxGroup>
 * ```
 *
 * @param props - CheckboxGroup properties
 */
const CheckboxGroup = ({
  label,
  description,
  errorMessage,
  className,
  children,
  ...props
}: CheckboxGroupProps) => (
  <AriaCheckboxGroup
    className={composeRenderProps(
      cn('@CheckboxGroup', className),
      (className) => cn(checkboxGroupVariants(), className),
    )}
    {...props}
  >
    {composeRenderProps(children, (children) => (
      <>
        <Label>{label}</Label>
        {children}
        {description && (
          <Text className="text-sm text-neutral/20" slot="description">
            {description}
          </Text>
        )}
        <FieldError>{errorMessage}</FieldError>
      </>
    ))}
  </AriaCheckboxGroup>
);

export { Checkbox, CheckboxGroup, checkboxVariants, checkboxGroupVariants };
export type { CheckboxProps, CheckboxGroupProps };
